import {push} from 'connected-react-router';
import {createSnackbar} from 'Core/ducks/snackbar';
import {AsyncResult} from 'Core/models/Async';
import {RoleId} from 'Core/models/Role';
import * as A from 'fp-ts/lib/Array';
import * as E from 'fp-ts/lib/Either';
import {eqNumber} from 'fp-ts/lib/Eq';
import {flow} from 'fp-ts/lib/function';
import * as NEA from 'fp-ts/lib/NonEmptyArray';
import * as O from 'fp-ts/lib/Option';
import {all, call, put, takeLatest} from 'redux-saga/effects';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import {eq} from 'Shared/utilities/equals';
import {get} from 'Shared/utilities/fetchHelpers';
import {mergeWithNextPage, removeApiPrefix} from 'Shared/utilities/PageUtility';
import {prop} from 'Shared/utilities/prop';
import {getPageApiCallGenerator, PageFetchAction} from 'Shared/utilities/sagaApiGenerators';
import {AgentRole, AgentUserDetailDto} from 'Users/api';
import agentApi from 'Users/api/agentApi';
import {AgentUserActionTypes, changeAgents, fetchAgentUsers, navigateToEdit} from 'Users/ducks/agentUser';
import {UserClaims} from 'Users/models/Claims';
import {UserModel} from 'Users/models/UserModel';
import {UserRouteConstants} from 'Users/Routes';

export const getAgentUsersFunction = (action: ReturnType<typeof fetchAgentUsers>) => {
  return PageFetchAction.match({
    Additional: additionalPage => mergeWithNextPage<UserModel>(additionalPage),
    Initial: () => agentApi.searchAgentUsers(action.payload.filter),
  })(action.payload);
};

export function* fetchAgentUsersSaga() {
  yield takeLatest(AgentUserActionTypes.FETCH_AGENTS, getPageApiCallGenerator(getAgentUsersFunction, changeAgents));
}

const getAgentDetailsUri = (encryptedIdentifier?: string) =>
  flow(
    (user: UserModel) => user.links,
    O.fromNullable,
    O.chain(NEA.fromArray),
    O.chain(NEA.filter(eq('rel', 'details'))),
    O.map(NEA.head),
    O.map(prop('href')),
    O.chain(O.fromNullable),
    O.map(removeApiPrefix),
    O.map(uri => `${uri}?${addFilterParameters({encryptedIdentifier}).slice(1)}`)
  );

const getIsAgentSupport = flow(
  (agentDetails: AgentUserDetailDto) => agentDetails.agencyAssociations,
  O.fromNullable,
  O.chain(NEA.fromArray),
  O.map(
    A.map(
      flow(
        prop('roles'),
        O.fromNullable,
        O.getOrElse(() => [] as AgentRole[])
      )
    )
  ),
  O.map(A.flatten),
  O.map(A.map(a => a.id!)),
  O.map(userRoleIds =>
    A.intersection(eqNumber)(
      [RoleId.AgencySupportWithAccounts, RoleId.AgencySupportWithCommissionsAnd1099s, RoleId.AgencySupportWithPolicies],
      userRoleIds
    )
  ),
  O.map(matchingSupportRoles => matchingSupportRoles.length > 0)
);

export function* navigateToEditSaga() {
  yield takeLatest(AgentUserActionTypes.NAVIGATE_TO_EDIT, function*(action: ReturnType<typeof navigateToEdit>) {
    const {agent, encryptedIdentifier} = action.payload;
    const agentDetailsUri = getAgentDetailsUri(encryptedIdentifier)(agent);
    if (O.isSome(agentDetailsUri)) {
      const agentDetailsResult: AsyncResult<UserClaims> = yield call(get, agentDetailsUri.value, false);
      if (E.isRight(agentDetailsResult)) {
        const isSupportAgentResult = getIsAgentSupport(agentDetailsResult.right);
        if (O.isSome(isSupportAgentResult)) {
          if (isSupportAgentResult.value) {
            yield put(push(UserRouteConstants.EDIT_SUPPORT_AGENT(agent.id!)));
          } else {
            yield put(push(UserRouteConstants.EDIT_AGENT(agent.id!)));
          }
        } else {
          yield put(createSnackbar('An Error Ocurred While Attempting to Edit the User.'));
        }
      } else {
        yield put(createSnackbar('An Error Ocurred While Attempting to Edit the User.'));
      }
    } else {
      yield put(createSnackbar('An Error Ocurred While Attempting to Edit the User.'));
    }
  });
}

export default function*() {
  yield all([fetchAgentUsersSaga(), navigateToEditSaga()]);
}
